import { useSignUp } from '@clerk/react'
import { zodResolver } from '@hookform/resolvers/zod'
import Input from 'components/input'
import { useShowSnack } from 'components/snackbar'
import { Controller, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import Button from 'components/button'
import { SizableText, XStack, YStack } from 'tamagui'
import { UserDetailsSchema, type UserDetailsFormData } from '../schemas'

interface Props {
    onVerificationNeeded: () => void
}

export const UserDetailsStep = ({ onVerificationNeeded }: Props) => {
    const { signUp, errors: clerkErrors } = useSignUp()
    const showSnack = useShowSnack()
    const navigate = useNavigate()

    const {
        control,
        handleSubmit,
        formState: { errors, isSubmitting },
    } = useForm<UserDetailsFormData>({
        resolver: zodResolver(UserDetailsSchema),
        defaultValues: {
            firstName: '',
            lastName: '',
            email: '',
            password: '',
            confirmPassword: '',
        },
    })

    const onSubmit = async (data: UserDetailsFormData) => {
        try {
            const createResult = await signUp.create({
                firstName: data.firstName,
                lastName: data.lastName,
                emailAddress: data.email,
            })
            if (createResult.error) {
                showSnack(createResult.error.message ?? 'Failed to create account', 'danger')
                return
            }
            const pwResult = await signUp.password({ password: data.password })
            if (pwResult.error) {
                showSnack(pwResult.error.message ?? 'Failed to set password', 'danger')
                return
            }
            const sendResult = await signUp.verifications.sendEmailCode()
            if (sendResult.error) {
                showSnack(sendResult.error.message ?? 'Failed to send verification code', 'danger')
                return
            }
            onVerificationNeeded()
        } catch (err: any) {
            const message =
                err?.errors?.[0]?.longMessage ?? err?.errors?.[0]?.message ?? 'Failed to create account'
            showSnack(message, 'danger')
        }
    }

    return (
        <form
            noValidate
            onSubmit={handleSubmit(onSubmit)}
        >
            <YStack gap="$4">
                <SizableText
                    fontSize="$8"
                    fontWeight="700"
                    letterSpacing={-0.3}
                >
                    Create your account
                </SizableText>
                <SizableText
                    color="$gray10"
                    fontSize="$4"
                >
                    Start your free 30 day trial
                </SizableText>

                <XStack gap="$4">
                    <Controller
                        name="firstName"
                        control={control}
                        render={({ field }) => (
                            <Input
                                label="First Name"
                                editMode
                                name="given-name"
                                autoComplete="given-name"
                                placeholder="John"
                                value={field.value}
                                onChange={field.onChange}
                                onBlur={field.onBlur}
                                error={errors.firstName?.message}
                                formControlProps={{ flex: 1 }}
                            />
                        )}
                    />
                    <Controller
                        name="lastName"
                        control={control}
                        render={({ field }) => (
                            <Input
                                label="Last Name"
                                editMode
                                name="family-name"
                                autoComplete="family-name"
                                placeholder="Smith"
                                value={field.value}
                                onChange={field.onChange}
                                onBlur={field.onBlur}
                                error={errors.lastName?.message}
                                formControlProps={{ flex: 1 }}
                            />
                        )}
                    />
                </XStack>

                <Controller
                    name="email"
                    control={control}
                    render={({ field }) => (
                        <Input
                            label="Email"
                            editMode
                            type="email"
                            name="email"
                            autoComplete="email"
                            placeholder="john@agency.com.au"
                            value={field.value}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            error={errors.email?.message}
                        />
                    )}
                />

                <Controller
                    name="password"
                    control={control}
                    render={({ field }) => (
                        <Input
                            label="Password"
                            editMode
                            type="password"
                            name="new-password"
                            autoComplete="new-password"
                            placeholder="Min 8 characters"
                            value={field.value}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            error={errors.password?.message}
                        />
                    )}
                />

                <Controller
                    name="confirmPassword"
                    control={control}
                    render={({ field }) => (
                        <Input
                            label="Confirm Password"
                            editMode
                            type="password"
                            name="new-password"
                            autoComplete="new-password"
                            value={field.value}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            error={errors.confirmPassword?.message}
                        />
                    )}
                />

                <Button
                    theme="primary"
                    // @ts-ignore - HTML form submit
                    type="submit"
                    loading={isSubmitting}
                    animation="quick"
                    hoverStyle={{ backgroundColor: '$backgroundHover' }}
                    pressStyle={{ backgroundColor: '$backgroundPress', opacity: 0.85 }}
                >
                    Continue
                </Button>

                <SizableText
                    textAlign="center"
                    color="$gray10"
                    fontSize="$3"
                >
                    Already have an account?{' '}
                    <SizableText
                        color="$primary"
                        cursor="pointer"
                        onPress={() => navigate('/')}
                    >
                        Sign in
                    </SizableText>
                </SizableText>
            </YStack>
        </form>
    )
}
